import React from 'react';
import PropTypes from 'prop-types';
import { Text } from '@reservamos/elements';
import { useTranslation } from 'react-i18next';
import useWhitelabelEnvs from 'hooks/whitelabel/useWhitelabelEnvs';
import getFixedFeeRows, { FIXED_FEE_LEGEND_KEY } from 'utils/purchase/getFixedFeeRows';
import PricingRow from '../../ui/atoms/PricingRow';

/**
 * PurchasePricingExtras component.
 * Renders a breakdown of additional costs like carbon credits, service fees, insurance, installment fees,
 * and exchange surcharges (e.g. route change, fare difference).
 * @param {object} props - Component props.
 * @param {number} props.carbon - Price of carbon offset credits
 * @param {number} props.total - Total price of all extra charges
 * @param {number} props.markup - Service fee markup amount
 * @param {number} props.insurance - Travel insurance cost
 * @param {number} props.ancillaries - Purchase total for products (snacks and the like)
 * @param {number} props.installmentsFee - Fee for installment payment plans
 * @param {object} props.serviceLevel - Selected service level details
 * @param {object} props.exchangeSurcharges - Exchange surcharges breakdown (items with amount, message, type; total)
 * @param {object} props.fixedFee - Platform-usage fee purchase totals ({ amount, tax }), already included in the purchase total
 * @returns {JSX.Element} Component that displays pricing extras breakdown
 */
const PurchasePricingExtras = ({
  carbon,
  total,
  markup,
  insurance,
  ancillaries,
  installmentsFee,
  serviceLevel,
  exchangeSurcharges,
  fixedFee,
}) => {
  const { t } = useTranslation('purchase');
  const { brand, fixedFee: fixedFeeConfig } = useWhitelabelEnvs();
  // The fee holds purchase totals here, so the rows need no per-seat multiplication.
  const fixedFeeRows = getFixedFeeRows({
    fee: fixedFee,
    configuredTaxPercent: fixedFeeConfig?.taxPercent,
  });

  const hasExchangeSurcharges =
    exchangeSurcharges?.total > 0 && Array.isArray(exchangeSurcharges?.items);
  // Every concept that can render a row gets its own clause here, including the ones already
  // inside the `extras` roll-up: `ancillaries` reaches `total` through the backend's `extras`,
  // and relying on that would tie this guard to a composition the funnel never verified.
  const hasExtras = total > 0 || ancillaries > 0 || hasExchangeSurcharges || Boolean(fixedFeeRows);
  if (!hasExtras) return null;

  return (
    <>
      {markup > 0 && (
        <PricingRow
          label={t('label.service_charge')}
          tooltipContent={t('text.working_for_a_better_shopping_experience')}
          price={markup}
          priceAlignment="flex-end"
        />
      )}

      {serviceLevel.total > 0 && (
        <PricingRow
          label={t(`service-levels.${serviceLevel.name}`, {
            context: 'title',
          })}
          price={serviceLevel.total}
          tooltipContent={t(`tooltip.service_level`, {
            context: serviceLevel?.name,
          })}
          priceAlignment="flex-end"
        />
      )}

      {hasExchangeSurcharges &&
        exchangeSurcharges.items.map((item, index) => (
          <PricingRow
            key={item?.id ?? `exchange-${item?.type ?? 'item'}-${index}`}
            label={item.message || item.type || t('label.exchange_surcharge')}
            price={Number(item.amount) || 0}
            priceAlignment="flex-end"
          />
        ))}

      {total > 0 && (
        <PricingRow label={t('label.additional')} price={total} isMain priceAlignment="flex-end" />
      )}

      {insurance > 0 && (
        <PricingRow
          label={t('label.additional_coverage', { context: brand })}
          price={insurance}
          priceAlignment="flex-end"
        />
      )}

      {ancillaries > 0 && (
        <PricingRow
          label={t('label.additional_services_row')}
          price={ancillaries}
          priceAlignment="flex-end"
        />
      )}

      {carbon > 0 && (
        <PricingRow
          label={t('label.carbon_offset', { context: brand })}
          price={carbon}
          priceAlignment="flex-end"
        />
      )}

      {installmentsFee > 0 && (
        <PricingRow
          label={t('label.deferred_payment_fee')}
          price={installmentsFee}
          priceAlignment="flex-end"
        />
      )}

      {fixedFeeRows && (
        <>
          {fixedFeeRows.map(({ labelKey, labelParams, amount }) => (
            <PricingRow
              key={labelKey}
              label={t(labelKey, labelParams)}
              price={amount}
              priceAlignment="flex-end"
            />
          ))}
          <Text size="XS" color="grayMedium">
            {t(FIXED_FEE_LEGEND_KEY)}
          </Text>
        </>
      )}
    </>
  );
};

PurchasePricingExtras.propTypes = {
  carbon: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  installmentsFee: PropTypes.number.isRequired,
  insurance: PropTypes.number.isRequired,
  ancillaries: PropTypes.number,
  markup: PropTypes.number.isRequired,
  serviceLevel: PropTypes.shape({
    name: PropTypes.string,
    total: PropTypes.number,
  }),
  exchangeSurcharges: PropTypes.shape({
    items: PropTypes.arrayOf(
      PropTypes.shape({
        amount: PropTypes.number,
        message: PropTypes.string,
        type: PropTypes.string,
      }),
    ),
    total: PropTypes.number,
  }),
  fixedFee: PropTypes.shape({
    amount: PropTypes.number,
    tax: PropTypes.number,
  }),
};

PurchasePricingExtras.defaultProps = {
  ancillaries: 0,
  serviceLevel: {
    name: 'basic',
    total: 0,
  },
  exchangeSurcharges: null,
  fixedFee: null,
};

export default PurchasePricingExtras;
